/**
 * @file downloads.member.config.js
 * @description What a MEMBER sees at /downloads (was /downloads/control).
 *
 * There was no such thing until now, and that was the bug. The Workspace nav
 * offered "Downloads" to every signed-in person and pointed at
 * /downloads/control — a path with no route at all. React Router found no
 * match and rendered the error state, so a member's first click on Downloads
 * was a broken page. The only downloads UI that existed was the admin console
 * at /admin/downloads/control, whose config is declared `role: 'admin'` and
 * whose tiles call adminActiveLinks() and adminStats().
 *
 * That is also the "active tabs give an error" report: "Active links" is a
 * signed-URL audit for operators. It was being offered to members, who get a
 * 403 the moment they open it.
 *
 * So this config is deliberately NARROW. One thing a member can do — see what
 * they have downloaded — and nothing that needs a permission they do not have.
 * It is not a cut-down admin console; it is the only screen a member needed.
 */
import { downloads } from '../../api/downloads';

const fmtDateTime = (raw) => {
  if (!raw) return '—';
  try { return new Date(raw).toLocaleString(); } catch { return String(raw); }
};

// The record calls it `bundleType` and spells it in snake_case. Both columns
// here read `kind`, a field that does not exist on a download job, so every row
// rendered an empty "What" — the one column that says which download it is.
const BUNDLE_LABELS = {
  project_bundle: 'Project bundle',
  project_source: 'Source code',
  project_assets: 'Assets',
  requirements_pdf: 'Requirements PDF',
  billing_invoice: 'Invoice',
  marketplace_bundle: 'Marketplace bundle',
  subscription_exclusive: 'Subscription download',
  org_bulk: 'Bulk download',
  internal_package: 'Internal package'
};

const fmtKind = (row) => {
  const raw = row?.bundleType || row?.kind;
  if (!raw) return '—';
  return BUNDLE_LABELS[raw] || String(raw).replace(/_/g, ' ');
};

export const downloadsMemberConfig = {
  serviceName: 'Downloads',
  basePath: '/downloads',
  // No `role` key: this is for anybody signed in. The admin config keeps its
  // own `role: 'admin'`, and the two must not be confused again — which is
  // why they are separate files rather than one config with a flag.

  nav: [
    { id: 'home',    label: 'Overview',     to: '' },
    { id: 'history', label: 'My downloads', to: '/history' }
    // Deliberately no "Active links". It is an operator audit of signed URLs
    // across the platform, and a member opening it gets a 403.
  ],

  home: {
    // Title and description are separate strings and must stay that way. They
    // were being read as one run-on line, which is what the "no space between
    // the headline and the sentence" report is: a heading and a sentence with
    // nothing telling them apart.
    summary: 'Every report and bundle you have downloaded, and how long each link stays valid.',
    primaryAction: { label: 'View my downloads', to: '/history' },
    metrics: [
      {
        key: 'history',
        label: 'Downloads so far',
        source: async () => {
          try {
            const data = await downloads.history({ page: 1, limit: 1 });
            if (typeof data?.total === 'number') return data.total;
            const items = Array.isArray(data) ? data : (data?.items || data?.history || []);
            return items.length;
          } catch {
            // A count that cannot be fetched shows as a dash rather than
            // taking the page down with it — the list below is the real
            // content and it can load perfectly well without this number.
            return '—';
          }
        }
      }
    ],
    quickActions: [
      { label: 'My downloads', description: 'Reports and bundles you have generated.', to: '/history', icon: 'datasets' }
    ]
  },

  entities: {
    history: {
      title: 'My downloads',
      subtitle: 'Reports and bundles you have generated. Links expire, so re-download rather than re-sharing an old one.',
      list: {
        endpoint: async ({ page, limit }) => {
          const data = await downloads.history({ page, limit });
          const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : Array.isArray(data?.history) ? data.history : []);
          return { items, pages: data?.pages };
        },
        searchable: false,
        // Says what to do next rather than only stating the absence. A first-
        // time member arriving here has downloaded nothing BY DEFINITION, so
        // this is the state most of them will meet first.
        emptyLabel: 'Nothing yet. Downloads appear here once you build a report and take a copy of it.',
        columns: [
          { key: 'bundleType', label: 'What',   render: fmtKind },
          { key: 'status',     label: 'Status' },
          { key: 'createdAt',  label: 'Created', render: (r) => fmtDateTime(r.createdAt) }
        ],
        rowActions: [
          { icon: 'eye', label: 'View', to: (r) => `/history/${encodeURIComponent(r._id || r.id || r.jobId)}` }
        ]
      },
      detail: {
        endpoint: (id) => downloads.status(id),
        backTo: '/history',
        titleField: (r) => (r ? `Download ${String(r._id || r.id || r.jobId).slice(-12)}` : 'Download'),
        // `completedAt` is real now. It was referenced here long before anything
        // populated it — the concept was right, the field just did not exist, so
        // the row could only ever render a dash.
        fields: [
          { key: 'bundleType',  label: 'What',   render: fmtKind },
          { key: 'status',      label: 'Status' },
          { key: 'fileName',    label: 'File' },
          { key: 'createdAt',   label: 'Created',   render: (r) => fmtDateTime(r.createdAt) },
          { key: 'completedAt', label: 'Completed', render: (r) => fmtDateTime(r.completedAt) },
          // Only meaningful for a stored artifact behind a link. A direct
          // download has none, so it is left out rather than shown as a dash.
          { key: 'expiresAt',   label: 'Link expires', render: (r) => (r?.expiresAt ? fmtDateTime(r.expiresAt) : 'Not a stored link') }
        ]
      }
    }
  }
};

export default downloadsMemberConfig;
